import { NgModule, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
registerLocaleData(localeAr);

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MultiTranslateHttpLoader, MyMissingTranslationHandler } from './helpers/MyMissingTranslationHandler';

import { MaterialModule } from './helpers/material-modules';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { LottieModule } from 'ngx-lottie';
import { playerFactory } from './shared/lottie-player-factory';

import { NgSelectModule } from '@ng-select/ng-select';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomPaginator } from './helpers/CustomPaginatorConfiguration';

import { ShortNamePipe } from './helpers/short-name.pipe';
import { TimeAgoPipe } from './helpers/time-ago.pipe';
import { SanitizeHtmlPipePipe } from './helpers/sanitize-html-pipe.pipe';
import { DropDownDirective } from './helpers/drop-down.directive';

import { JwtInterceptor } from './helpers/jwt.interceptor';
import { ErrorInterceptor } from './helpers/error.interceptor';
import { ActivatedRoute } from '@angular/router';

// import { OfflineInterceptor } from './helpers/offline.interceptor';

import { BuildDetailsHttpService } from './helpers/BuildDetails/build-details.http.service';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { HeaderComponent } from './layouts/admin-layout/header/header.component';
import { SidebarComponent } from './layouts/admin-layout/sidebar/sidebar.component';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { EmptyLayoutComponent } from './layouts/empty-layout/empty-layout.component';
import { ActionsBarComponent } from './layouts/admin-layout/actions-bar/actions-bar.component';
import { TillAmountConfirmComponent } from './layouts/admin-layout/actions-bar/components/till-amount-confirm/till-amount-confirm.component';
import { TranslationLoadedService } from './services/TranslationLoadedService';
export function fetchBuildDetails(buildDetailsService: BuildDetailsHttpService) {
  return () => buildDetailsService.fetchBuildDetails();
}
export function HttpLoaderFactory(httpClient: HttpClient, translationLoaded: TranslationLoadedService): TranslateLoader {
  return new MultiTranslateHttpLoader(httpClient, translationLoaded);
  // return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}
export function initializeTranslations(translationLoadedService: TranslationLoadedService): () => Promise<void> {
  return async (): Promise<void> => {
    return new Promise(resolve => {
      translationLoadedService.loaded$().subscribe(() => resolve());
    });
  };
}

@NgModule({
  declarations: [
    AppComponent,

    BasicLayoutComponent,
    EmptyLayoutComponent,
    AdminLayoutComponent,
    HeaderComponent,
    SidebarComponent,
    ActionsBarComponent,
    TillAmountConfirmComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    NgSelectModule,
    LottieModule.forRoot({ player: playerFactory }),
    ShortNamePipe,
    TimeAgoPipe,
    SanitizeHtmlPipePipe,
    DropDownDirective,
    TranslateModule.forRoot({
      defaultLanguage: localStorage.getItem('language') || 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (HttpLoaderFactory),
        deps: [HttpClient, TranslationLoadedService]
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
    })
  ],
  providers: [
    { provide: LOCALE_ID, useValue: localStorage.getItem('language') || 'en' },
    { provide: MatPaginatorIntl, useValue: CustomPaginator() },
    { provide: APP_INITIALIZER, useFactory: initializeTranslations, deps: [TranslationLoadedService], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptor, multi: true },
    // { provide: APP_INITIALIZER, useFactory: fetchBuildDetails, deps: [BuildDetailsHttpService], multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    private activatedRoute: ActivatedRoute,
    public translate: TranslateService
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['lang'] && (params['lang'] == 'ar' || 'en')) {
        if (localStorage.getItem('language') !== params['lang']) {
          localStorage.setItem('language', params['lang']);
          translate.use(params['lang']);
          location.reload();
        }
      }
    });
  }
}
